const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

const TOKEN_KEY = 'geo_tokens';

interface StoredTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
}

export function getLoginUrl(): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
  });
  return `https://${COGNITO_DOMAIN}/login?${params}`;
}

export function getLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: window.location.origin,
  });
  return `https://${COGNITO_DOMAIN}/logout?${params}`;
}

export async function exchangeCodeForTokens(code: string): Promise<StoredTokens> {
  const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
    }),
  });

  if (!res.ok) throw new Error('Token exchange failed');

  const data: TokenResponse = await res.json();
  const tokens: StoredTokens = {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };

  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  return tokens;
}

export function getStoredTokens(): StoredTokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  return JSON.parse(raw);
}

export function isTokenExpired(): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return true;
  // Refresh 60s before actual expiry
  return Date.now() >= tokens.expiresAt - 60_000;
}

export async function refreshAccessToken(): Promise<StoredTokens | null> {
  const tokens = getStoredTokens();
  if (!tokens?.refreshToken) {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }

  try {
    const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: tokens.refreshToken,
      }),
    });

    if (!res.ok) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }

    const data: TokenResponse = await res.json();
    const updated: StoredTokens = {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: tokens.refreshToken, // Cognito doesn't return a new refresh token
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    localStorage.setItem(TOKEN_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
}

export async function getAccessToken(): Promise<string | null> {
  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    return refreshed?.accessToken || null;
  }
  return getStoredTokens()?.accessToken || null;
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return false;
  // Considered authenticated if we have a valid token or a refresh token to renew it
  return !isTokenExpired() || !!tokens.refreshToken;
}
